import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
    AssetAllocationApi,
    AssetAllocationOverrideItem,
    AssetAllocationPage,
    Configuration,
    Filter,
    FilterType,
} from '@/cip/_openapi/portfolio-optimizer';
import { defaultApiConfig } from '@/cip/api/defaultApiConfig';
import { Toast } from 'widgets';

const getAssetAllocationApi = () => new AssetAllocationApi(new Configuration(defaultApiConfig));

const QueryKeys = {
    list: (params: FundAssetAllocationsParams) => ['fund-asset-allocations', 'list', params] as const,
    all: () => ['fund-asset-allocations'] as const,
};

export interface FundAssetAllocationsParams {
    pageIndex: number;
    pageSize: number;
    search?: string;
    hasUncategorized?: boolean;
    hasOverrides?: boolean;
    hasHoldings?: boolean;
    sortLabel?: string;
    sortDesc?: boolean;
}

const SEARCH_FIELDS = ['symbol', 's_id', 'description', 'cusip'] as const;

const buildSearchFilters = (search: string | undefined): Filter[] | undefined => {
    const trimmed = search?.trim();
    if (!trimmed) return undefined;
    // LIST wraps STRING children as an OR across SEARCH_FIELDS.
    return [
        {
            type: FilterType.LIST,
            field: '', // Unused for LIST type; children carry the actual fields.
            value: SEARCH_FIELDS.map((field) => ({ type: FilterType.STRING, field, value: trimmed })),
        },
    ];
};

export const useFundAssetAllocations = (params: FundAssetAllocationsParams) =>
    useQuery<AssetAllocationPage>({
        queryKey: QueryKeys.list(params),
        placeholderData: keepPreviousData,
        queryFn: () =>
            getAssetAllocationApi().getAssetAllocations({
                pageIndex: params.pageIndex,
                pageSize: params.pageSize,
                hasUncategorizedExposure: params.hasUncategorized || undefined,
                hasActiveOverride: params.hasOverrides || undefined,
                hasHoldings: params.hasHoldings || undefined,
                sortLabel: params.sortLabel,
                sortDesc: params.sortDesc,
                filter: buildSearchFilters(params.search),
            }),
    });

export const useSetAssetAllocationOverride = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ sId, overrides }: { sId: string; overrides: AssetAllocationOverrideItem[] }) =>
            getAssetAllocationApi().setAssetAllocationOverride({
                sId,
                assetAllocationOverrideRequest: { overrides },
            }),
        onSuccess: (_data, { sId }) => {
            toast.success(`Asset allocation overrides saved for ${sId}`);
            queryClient.invalidateQueries({ queryKey: QueryKeys.all() });
        },
        onError: (error) => Toast.forError(error, 'Failed to save asset allocation overrides'),
    });
};

export const useDeleteAssetAllocationOverride = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ sId }: { sId: string }) => getAssetAllocationApi().deleteAssetAllocationOverride({ sId }),
        onSuccess: (_data, { sId }) => {
            toast.success(`Asset allocation overrides cleared for ${sId}`);
            queryClient.invalidateQueries({ queryKey: QueryKeys.all() });
        },
        onError: (error) => Toast.forError(error, 'Failed to clear asset allocation overrides'),
    });
};
